import { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from '../ui/sheet';
import {
  MessageCircle, Send, Loader2, BookOpen, ChevronRight,
  Sparkles, X, RotateCcw, HelpCircle, Lightbulb
} from 'lucide-react';
import { useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RetailerCopilot() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [knowledgeBase, setKnowledgeBase] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (isOpen && !knowledgeBase) {
      fetchKnowledgeBase();
    }
  }, [isOpen]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchKnowledgeBase = async () => {
    try {
      const res = await fetch(`${API_URL}/api/retailer-copilot/knowledge-base`);
      if (res.ok) {
        const data = await res.json();
        setKnowledgeBase(data);
        setSuggestedQuestions(data.suggested_questions || []);
      }
    } catch (e) {
      console.error('Failed to fetch knowledge base:', e);
    }
  };

  const handleSend = async (text) => {
    const message = text || inputValue.trim();
    if (!message || loading) return;

    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: message }]);
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/retailer-copilot/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          session_id: sessionId,
          context: { current_path: location.pathname }
        })
      });

      if (res.ok) {
        const data = await res.json();
        setSessionId(data.session_id);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.response
        }]);
        if (data.suggested_questions?.length) {
          setSuggestedQuestions(data.suggested_questions);
        }
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try rephrasing your question.'
        }]);
      }
    } catch (e) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Unable to connect. Please check your internet connection and try again.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleNewConversation = () => {
    setMessages([]);
    setSessionId(null);
  };

  const renderMarkdown = (text) => {
    if (!text) return '';
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n(\d+)\.\s/g, '\n<br/><strong>$1.</strong> ')
      .replace(/\n-\s/g, '\n<br/>• ')
      .replace(/\n/g, '<br/>');
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <button
            data-testid="retailer-copilot-trigger"
            className="fixed bottom-20 right-6 z-50 bg-purple-600 hover:bg-purple-700 text-white rounded-full p-4 shadow-lg transition-all hover:scale-105 active:scale-95"
            onClick={() => setIsOpen(true)}
          >
            <div className="relative">
              <Sparkles className="w-6 h-6" />
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full border-2 border-purple-600" />
            </div>
          </button>
        </SheetTrigger>

        <SheetContent
          side="right"
          className="w-full sm:w-[420px] p-0 flex flex-col"
          data-testid="retailer-copilot-panel"
        >
          <SheetHeader className="px-4 pt-4 pb-2 border-b bg-gradient-to-r from-purple-50 to-indigo-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="bg-purple-600 p-1.5 rounded-lg">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div>
                  <SheetTitle className="text-base font-semibold">Retailer Copilot</SheetTitle>
                  <SheetDescription className="text-xs">AI-powered support for your store</SheetDescription>
                </div>
              </div>
            </div>

            <div className="flex gap-1 mt-2">
              <button
                data-testid="copilot-tab-chat"
                onClick={() => { setActiveTab('chat'); setSelectedSection(null); }}
                className={`flex-1 py-1.5 px-3 text-xs font-medium rounded-md transition-colors ${
                  activeTab === 'chat'
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                <MessageCircle className="w-3 h-3 inline mr-1" />
                Ask Copilot
              </button>
              <button
                data-testid="copilot-tab-guide"
                onClick={() => { setActiveTab('guide'); setSelectedSection(null); }}
                className={`flex-1 py-1.5 px-3 text-xs font-medium rounded-md transition-colors ${
                  activeTab === 'guide'
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                <BookOpen className="w-3 h-3 inline mr-1" />
                Guide
              </button>
            </div>
          </SheetHeader>

          {activeTab === 'chat' && (
            <div className="flex-1 flex flex-col overflow-hidden">
              <ScrollArea className="flex-1 p-4" ref={scrollRef}>
                {messages.length === 0 ? (
                  <div className="space-y-4">
                    <div className="text-center py-6">
                      <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Sparkles className="w-6 h-6 text-purple-600" />
                      </div>
                      <p className="text-sm font-medium text-gray-700">How can I help you today?</p>
                      <p className="text-xs text-gray-500 mt-1">Ask about scanning, posting deals, orders, delivery, and more</p>
                    </div>

                    <div className="space-y-2">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Suggested Questions</p>
                      {suggestedQuestions.slice(0, 6).map((q, i) => (
                        <button
                          key={i}
                          data-testid={`suggested-question-${i}`}
                          onClick={() => handleSend(q)}
                          className="w-full text-left p-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-purple-50 hover:border-purple-200 transition-colors flex items-center gap-2"
                        >
                          <Lightbulb className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {messages.map((msg, i) => (
                      <div
                        key={i}
                        data-testid={`copilot-message-${i}`}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                          msg.role === 'user'
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {msg.role === 'assistant' ? (
                            <div
                              className="copilot-response prose prose-sm max-w-none"
                              dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
                            />
                          ) : (
                            msg.content
                          )}
                        </div>
                      </div>
                    ))}
                    {loading && (
                      <div className="flex justify-start">
                        <div className="bg-gray-100 rounded-lg px-3 py-2 flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
                          <span className="text-sm text-gray-500">Thinking...</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </ScrollArea>

              <div className="border-t p-3 bg-white">
                {messages.length > 0 && (
                  <button
                    onClick={handleNewConversation}
                    className="text-xs text-purple-600 hover:text-purple-700 mb-2 flex items-center gap-1"
                    data-testid="copilot-new-conversation"
                  >
                    <RotateCcw className="w-3 h-3" /> New conversation
                  </button>
                )}
                <div className="flex gap-2">
                  <Input
                    ref={inputRef}
                    data-testid="copilot-input"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                    placeholder="Ask about scanning, orders, delivery..."
                    className="flex-1 text-sm"
                    disabled={loading}
                  />
                  <Button
                    data-testid="copilot-send"
                    onClick={() => handleSend()}
                    disabled={loading || !inputValue.trim()}
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'guide' && (
            <ScrollArea className="flex-1 p-4">
              {selectedSection ? (
                <div>
                  <button
                    onClick={() => setSelectedSection(null)}
                    className="text-xs text-purple-600 hover:text-purple-700 mb-3 flex items-center gap-1"
                    data-testid="guide-back"
                  >
                    <ChevronRight className="w-3 h-3 rotate-180" /> Back to all sections
                  </button>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{selectedSection.title}</h3>
                  <div
                    className="text-sm text-gray-700 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(selectedSection.content) }}
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold text-gray-700">Retailer Onboarding Guide</p>
                    <Badge variant="secondary" className="text-xs">V1.1</Badge>
                  </div>
                  {knowledgeBase?.sections?.map((section, i) => (
                    <button
                      key={section.id}
                      data-testid={`guide-section-${section.id}`}
                      onClick={() => setSelectedSection(section)}
                      className="w-full text-left p-3 bg-white border border-gray-200 rounded-lg hover:bg-purple-50 hover:border-purple-200 transition-colors flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className="bg-purple-100 text-purple-600 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold">
                          {i + 1}
                        </div>
                        <span className="text-sm font-medium text-gray-700">{section.title}</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
